<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

import { getDownloadUrl } from "@/api/client";
import DrawingAnnotationOverlay from "@/components/DrawingAnnotationOverlay.vue";
import type { Shape } from "@/lib/drawingMarkup";

const props = defineProps<{ drawing: Record<string, unknown> }>();

const url = ref<string | null>(null);
const failed = ref(false);

onMounted(async () => {
  const fileId = props.drawing.file_id as string | undefined;
  if (!fileId) {
    failed.value = true;
    return;
  }
  try {
    url.value = await getDownloadUrl(fileId);
  } catch {
    failed.value = true;
  }
});

interface Stroke {
  color?: string;
  points?: number[][];
}

// Legacy freehand markup (0-1 coords) — rendered with a 0..1 viewBox SVG.
const strokes = computed<Stroke[]>(() => {
  const m = props.drawing.markup as { strokes?: Stroke[] } | null | undefined;
  return Array.isArray(m?.strokes) ? (m!.strokes as Stroke[]) : [];
});

// Rich markup from the Drawings register — the full shape set. Rendered in
// pixel space via the shared overlay, so it needs the rendered image size.
const shapes = computed<Shape[]>(() => {
  const m = props.drawing.markup as { shapes?: Shape[] } | null | undefined;
  return Array.isArray(m?.shapes) ? (m!.shapes as Shape[]) : [];
});

const imgEl = ref<HTMLImageElement | null>(null);
const boxW = ref(0);
const boxH = ref(0);
let ro: ResizeObserver | null = null;
function measure(): void {
  const el = imgEl.value;
  if (el) {
    boxW.value = el.clientWidth;
    boxH.value = el.clientHeight;
  }
}
onMounted(() => {
  ro = new ResizeObserver(measure);
});
onBeforeUnmount(() => ro?.disconnect());
function onImgLoad(): void {
  measure();
  if (imgEl.value && ro) ro.observe(imgEl.value);
}

const polyline = (s: Stroke): string =>
  (s.points ?? []).map((p) => `${p[0]},${p[1]}`).join(" ");

const title = computed(() => String(props.drawing.title ?? "Drawing"));
</script>

<template>
  <figure class="overflow-hidden rounded-lg border border-neutral-200">
    <div class="relative bg-neutral-100">
      <img
        v-if="url"
        ref="imgEl"
        :src="url"
        :alt="title"
        class="block w-full"
        @load="onImgLoad"
      >
      <div
        v-else
        class="flex h-40 items-center justify-center text-sm text-neutral-400"
      >
        {{ failed ? "Image unavailable" : "Loading…" }}
      </div>
      <!-- Legacy freehand strokes -->
      <svg
        v-if="url && strokes.length"
        class="pointer-events-none absolute inset-0 h-full w-full"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
      >
        <polyline
          v-for="(s, i) in strokes"
          :key="i"
          :points="polyline(s)"
          :stroke="s.color || '#AD2D1D'"
          fill="none"
          stroke-width="0.004"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <!-- Rich shapes (from the Drawings register) -->
      <DrawingAnnotationOverlay
        v-if="url && shapes.length && boxW > 0"
        :shapes="shapes"
        :w="boxW"
        :h="boxH"
      />
    </div>
    <figcaption class="flex items-center justify-between gap-2 px-3 py-2 text-sm">
      <span class="font-medium text-neutral-900">{{ title }}</span>
      <slot name="action" />
    </figcaption>
  </figure>
</template>
